import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import Navbar from '../components/Navbar';
import Footer from '../components/sections/Footer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { getProjects } from '../services/api';

const Projects = () => {
    const { t, language } = useLanguage();
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const data = await getProjects();
                setProjects(data);
            } catch (error) {
                console.error("Failed to fetch projects:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
        window.scrollTo(0, 0);
    }, []);

    const getLocalized = (field) => {
        if (typeof field === 'object' && field !== null) {
            return field[language] || field['en'] || '';
        }
        return field;
    };

    return (
        <div className="bg-white min-h-screen flex flex-col">
            <Navbar forceSolid={true} />

            <div className="flex-grow pt-32 pb-16 container mx-auto px-4">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">{t.nav.projects}</h1>
                    <p className="text-slate-600 text-lg">
                        {language === 'sr'
                            ? 'Pregled svih naših realizovanih projekata i trenutnih radova.'
                            : 'Overview of all our realized projects and current works.'}
                    </p>
                </div>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-500 mx-auto"></div>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {projects.map((project, index) => (
                            <Card
                                key={project.id || index}
                                className="group cursor-pointer h-full flex flex-col"
                                onClick={() => setSelectedProject(project)}
                            >
                                <div className="relative h-64 overflow-hidden flex-shrink-0">
                                    <img
                                        src={project.image}
                                        alt={getLocalized(project.title)}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                        <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                                            {t.portfolio.viewDetails}
                                        </Button>
                                    </div>
                                </div>
                                <div className="p-6 flex-grow flex flex-col">
                                    <span className="text-gold-600 font-semibold text-sm tracking-wider uppercase mb-2 block">
                                        {getLocalized(project.category)}
                                    </span>
                                    <h3 className="text-2xl font-bold mb-3 text-slate-900 group-hover:text-gold-600 transition-colors">
                                        {getLocalized(project.title)}
                                    </h3>
                                    <p className="text-slate-600 line-clamp-3 mb-4 flex-grow">
                                        {getLocalized(project.description)}
                                    </p>
                                    <div className="pt-4 border-t border-slate-100 mt-auto flex justify-between items-center text-sm">
                                        <span className="text-slate-500">{project.year}</span>
                                        <span className={`font-medium ${getLocalized(project.status) === 'U toku' || getLocalized(project.status) === 'In Progress' ? 'text-gold-600' : 'text-green-600'}`}>
                                            {getLocalized(project.status)}
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}
            </div>

            {/* Project Details Modal (Same as Portfolio) */}
            {selectedProject && (
                <Modal isOpen={!!selectedProject} onClose={() => setSelectedProject(null)}>
                    <div className="relative h-64 md:h-80">
                        <img
                            src={selectedProject.image}
                            alt="Project Loading"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-8">
                            <div className="w-full">
                                <span className="text-gold-500 font-bold tracking-wider uppercase mb-2 block text-sm">
                                    {getLocalized(selectedProject.category)}
                                </span>
                                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                                    {getLocalized(selectedProject.title)}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div className="p-8">
                        <div className="mb-8">
                            <h4 className="text-xl font-bold text-slate-900 mb-4">{t.portfolio.overview}</h4>
                            <p className="text-slate-600 leading-relaxed text-lg line-clamp-4">
                                {getLocalized(selectedProject.description)}
                            </p>
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                            <div className="space-y-1">
                                <p className="text-sm text-slate-500">Status</p>
                                <p className="font-semibold text-green-600">
                                    {getLocalized(selectedProject.status) || 'Completed'}
                                </p>
                            </div>

                            <a
                                href={`/project/${selectedProject.id}`}
                                className="bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gold-600 transition-colors inline-block"
                            >
                                {t.portfolio.viewDetails}
                            </a>
                        </div>
                    </div>
                </Modal>
            )}

            <Footer />
        </div>
    );
};

export default Projects;
